/**
 * The chapter grammar (ADR 2, decisions 35 and 36): CommonMark with every code form, raw HTML and
 * autolinks off, and the book extensions on, GFM footnotes among them. GFM's table, strikethrough, task-list and bare-URL extensions
 * are never loaded. The one tree it produces feeds the editor, layout and exporters.
 *
 * Off, and what the text parses as instead:
 * - indented code: a paragraph (`plainParagraph`, which also caps block nesting);
 * - fenced code and code spans: literal backticks and tildes, never a paragraph break;
 * - HTML blocks, inline HTML, HTML comments and `<scheme:…>` / `<a@b>` autolinks: literal text, never a
 *   paragraph break. Entities (`&amp;`) stay: they are CommonMark text, and the stored form escapes `&`.
 *
 * `patches/@lezer__markdown@1.7.2.patch` changes Lezer itself (part 02 log, "Lezer patch"): a removed
 * block no longer interrupts paragraphs (Lezer kept the fenced-code and HTML-block rules); inline parsing,
 * link and reference-definition scanning and quote markers are linear in the paragraph instead of
 * quadratic, with the same results; inline markup nests at most 32 containers deep; and an edit at the
 * document start no longer reuses a block whose type its indentation decided.
 */
import { parser as commonMark } from '@lezer/markdown';
import type { MarkdownParser } from '@lezer/markdown';
import { centered } from './centered.ts';
import { footnotes } from './footnotes.ts';
import { plainParagraph } from './plain-paragraph.ts';
import { sceneBreak } from './scene-break.ts';
import { smallCaps } from './small-caps.ts';

/** Lezer parsers the grammar removes (decisions 35, 36). */
export const REMOVED_PARSERS = ['IndentedCode', 'FencedCode', 'HTMLBlock', 'HorizontalRule', 'InlineCode', 'HTMLTag'] as const;

/**
 * Node types a chapter tree never contains: the removed constructs and their parts. Lezer still defines
 * them, so every test of the grammar checks their absence.
 */
export const ABSENT_NODES = [
  'CodeBlock',
  'FencedCode',
  'CodeMark',
  'CodeText',
  'CodeInfo',
  'InlineCode',
  'HTMLBlock',
  'HTMLTag',
  'CommentBlock',
  'Comment',
  'ProcessingInstructionBlock',
  'ProcessingInstruction',
  'Autolink',
  'HorizontalRule',
] as const;

/** The configured parser. Pure and shareable: Lezer parsers hold no per-parse state. */
export const chapterParser: MarkdownParser = commonMark.configure([
  { remove: REMOVED_PARSERS },
  plainParagraph,
  sceneBreak,
  centered,
  smallCaps,
  footnotes,
]);
